import { Dispatch, SetStateAction } from 'react';
import { useIntl } from 'react-intl';
import { IconGripVertical, IconTrash } from '@rossum/ui/icons/tabler';
import {
  Box,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import SortableWrapper from '../../../../../../decorators/SortableWrapper/SortableWrapper';
import {
  insertInArray,
  removeIndexInArray,
  updateInArray,
} from '../../../../../../redux/modules/utils';
import { EnumOption } from '../../../../../../types/schema';
import { OPTION_INPUT_WIDTH, optionRowSx } from './enumEditorSx';

const defaultOption = { value: '', label: '' };

type EnumEditorItemProps = {
  enumId: string;
  enumIndex: number;
  enumItem: EnumOption;
  optionsAreEmpty: boolean;
  options: Array<EnumOption>;
  currentLine?: number;
  setCurrentLine: Dispatch<SetStateAction<number | undefined>>;
  onCurrentSchemaPartChange: (_options: Array<EnumOption>) => void;
};

const EnumEditorItem = ({
  enumId,
  enumIndex,
  enumItem,
  currentLine,
  setCurrentLine,
  optionsAreEmpty,
  options,
  onCurrentSchemaPartChange,
}: EnumEditorItemProps) => {
  const intl = useIntl();
  const onChange =
    (index: number, type: 'value' | 'label') => (value: string) =>
      onCurrentSchemaPartChange(
        updateInArray(options, index, _option => ({
          ..._option,
          [type]: value,
        }))
      );

  const onTmpChange = (type: 'value' | 'label') => (value: string) => {
    const newOption = { ...defaultOption, [type]: value };
    onCurrentSchemaPartChange(insertInArray(options, 0, newOption));
  };

  const onRemove = (index: number) => () =>
    onCurrentSchemaPartChange(removeIndexInArray(options, index));

  return (
    <SortableWrapper
      id={enumId}
      render={sortableProps => (
        <Stack direction="row" sx={optionRowSx}>
          <Box
            sx={{
              margin: '12px 4px 12px 0',
              '& svg': { fill: t => t.palette.text.secondary },
            }}
          >
            <SvgIcon
              fontSize="inherit"
              {...sortableProps?.listeners}
              {...sortableProps?.attributes}
              sx={{
                cursor: 'grab',
                ':focus': {
                  outline: 'none',
                  cursor: 'grabbing',
                },
              }}
            >
              <IconGripVertical />
            </SvgIcon>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              autoFocus={enumIndex === currentLine}
              sx={{ width: OPTION_INPUT_WIDTH }}
              onChange={({ target: { value } }) =>
                optionsAreEmpty
                  ? onTmpChange('value')(value)
                  : onChange(enumIndex, 'value')(value)
              }
              onFocus={() => setCurrentLine(enumIndex)}
              placeholder={intl.formatMessage({
                id: 'components.enumEditor.id.placeholder',
              })}
              value={enumItem.value}
            />
            <TextField
              size="small"
              sx={{ width: OPTION_INPUT_WIDTH }}
              onChange={({ target: { value } }) =>
                optionsAreEmpty
                  ? onTmpChange('label')(value)
                  : onChange(enumIndex, 'label')(value)
              }
              onFocus={() => setCurrentLine(enumIndex)}
              placeholder={intl.formatMessage({
                id: 'components.enumEditor.label.placeholder',
              })}
              value={enumItem.label}
            />
          </Stack>
          <IconButton
            onClick={onRemove(enumIndex)}
            color="error"
            sx={{ ml: 1 }}
          >
            <SvgIcon fontSize="medium">
              <IconTrash />
            </SvgIcon>
          </IconButton>
        </Stack>
      )}
    />
  );
};

export default EnumEditorItem;
